/* eslint-disable check-file/filename-naming-convention */
import { XMLParser } from 'fast-xml-parser';
import JSZip from 'jszip';
import { reduceToMap, parseAsArray, parseVec3DArray } from './arrayHelpers';
import { getFirstChildOrUndefined } from './xmlHelper';
import { CADBody, CADBodyInstance, CADComponent, CADComponentInstance, CADVisual3D, CADModel } from './types';
import { computeCenter } from './meshHelpers/computeCenter';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawXML = any;

/**
 * Internal type used while parsing the xml, It removes the instance and adds the two links
 */
type InstanceLink<T extends { instance: unknown }> = Omit<T, 'instance'> & {
  isAggregatedBy: number;
  isInstanceOf: number;
};

/**
 * The xml parse with the settings used throughout reading the 3dxml file
 */
const xmlParser = new XMLParser({
  ignoreAttributes: false,
});
/**
 * Parses the manifest file of the 3dxml
 * @param filePointer The zip file pointing to 3dxmls' manifest file
 * @returns The reference to the root file
 */
async function parseManifestFile(filePointer: JSZip.JSZipObject | null): Promise<{ rootFileName: string }> {
  if (!filePointer) {
    throw new Error('3DXML does not contain a manifest file');
  }
  const obj = xmlParser.parse(await filePointer.async('string'));

  return { rootFileName: obj.Manifest.Root };
}
/**
 * Parses the root file
 * @param filePointer The zip file pointer to the root file
 * @returns The parsed product structure
 */
async function parseRootFile(filePointer: JSZip.JSZipObject | null): Promise<{
  rootId: number;
  reference3Ds: Record<number, Omit<CADComponent, 'children' | 'representations'>>;
  instance3Ds: Record<number, InstanceLink<CADComponentInstance>>;
  referenceReps: Record<number, Omit<CADBody, 'vertexBuffer' | 'faces'> & { file: string }>;
  instanceReps: Record<number, InstanceLink<CADBodyInstance>>;
}> {
  if (!filePointer) {
    throw new Error('Root file does not exist');
  }
  const obj = xmlParser.parse(await filePointer.async('string'));
  const structure = obj.Model_3dxml.ProductStructure;

  return {
    rootId: Number(structure['@_root']),
    reference3Ds: reduceToMap(
      structure.Reference3D.map((v: RawXML) => ({
        id: Number(v['@_id']),
        name: v['@_name'],
      })),
    ),
    instance3Ds: reduceToMap(
      structure.Instance3D.map((v: RawXML) => ({
        id: Number(v['@_id']),
        name: v['@_name'],
        isAggregatedBy: Number(v['IsAggregatedBy']),
        isInstanceOf: Number(v['IsInstanceOf']),
        relativeMatrix: v['RelativeMatrix'].split(' ').map(Number),
      })),
    ),
    referenceReps: reduceToMap(
      structure.ReferenceRep.map((v: RawXML) => ({
        id: Number(v['@_id']),
        name: v['@_name'],
        file: v['@_associatedFile'].replace('urn:3DXML:', ''),
      })),
    ),
    instanceReps: reduceToMap(
      structure.InstanceRep.map((v: RawXML) => ({
        id: Number(v['@_id']),
        name: v['@_name'],
        isAggregatedBy: Number(v['IsAggregatedBy']),
        isInstanceOf: Number(v['IsInstanceOf']),
      })),
    ),
  };
}

/**
 * Converts multiple Fans (3D visualization technique) to individual triangles as used in modern
 * 3D engines.
 * @param fans The fans to convert to triangle sets
 * @returns the indices generated for the given fans
 */
function convertFan2Triangles(fans: number[][] | undefined): number[] {
  if (!fans) {
    return [];
  }
  return fans.flatMap((fan) => {
    const result: number[] = [];
    for (let i = 2; i < fan.length; i++) {
      result.push(fan[0], fan[i - 1], fan[i]);
    }
    return result;
  });
}
/**
 * Converts multiple Strips (3D visualization technique) to individual triangles as used in modern
 * 3D engines.
 * @param strips The strips to convert to triangle sets
 * @returns the indices generated for the given strip
 */
function convertStrip2Triangles(strips: number[][] | undefined): number[] {
  if (!strips) {
    return [];
  }
  return strips.flatMap((strip) => {
    const result = [];
    for (let i = 2; i < strip.length; i++) {
      if (i % 2 === 0) {
        result.push(strip[i - 1], strip[i - 2], strip[i]);
      } else {
        result.push(strip[i - 2], strip[i - 1], strip[i]);
      }
    }
    return result;
  });
}

/**
 * Parses the face from the raw xml object
 * @param xmlObj The xml object to parse
 * @returns The triangles used for this face
 */
function parseFace(xmlObj: RawXML): CADVisual3D['faces'][number] {
  // parse triangles
  const triangles1 = xmlObj['@_triangles']?.split(' ').map(Number);
  // parse fans
  const triangles2 = convertFan2Triangles(
    xmlObj['@_fans']?.split(',').map((fan: string) => fan.split(' ').map(Number)),
  );
  // parse strips
  const triangles3 = convertStrip2Triangles(
    xmlObj['@_strips']?.split(',').map((fan: string) => fan.split(' ').map(Number)),
  ).reverse();
  // merge together
  const indices = new Uint16Array(([] as number[]).concat(triangles1).concat(triangles2).concat(triangles3));

  // get the color
  const surfaceAttributeObj = getFirstChildOrUndefined(xmlObj, 'SurfaceAttributes');
  const colorObj = getFirstChildOrUndefined(surfaceAttributeObj, 'Color');

  let color = {
    r: 1,
    g: 1,
    b: 1,
  };
  if (colorObj && '@_red' in colorObj && '@_green' in colorObj && '@_blue' in colorObj) {
    color = {
      r: colorObj['@_red'],
      g: colorObj['@_green'],
      b: colorObj['@_blue'],
    };
  }

  return {
    indices,
    color,
  };
}
/**
 * Parses the 3d representation file by extracting the vertex buffer, and faces.
 * @param raw Pointer to the file containing the 3D representation
 * @returns The 3D representation
 */
async function parse3DRepFile(raw: JSZip.JSZipObject | null): Promise<CADVisual3D[]> {
  if (!raw) {
    throw new Error('3DRep file does not exist');
  }
  const obj = xmlParser.parse(await raw.async('string'));

  function recurseToRepresentations(xmlNode: RawXML): RawXML[] {
    const asArray = parseAsArray(xmlNode);
    const result: RawXML[] = [];
    for (const obj of asArray) {
      if (obj['@_xsi:type'] === 'BagRepType') {
        result.push(...recurseToRepresentations(obj['Rep']));
      } else {
        result.push(obj);
      }
    }
    return result;
  }
  if (!('XMLRepresentation' in obj)) {
    return [];
  }

  const representations = recurseToRepresentations(obj['XMLRepresentation']['Root']);

  const nonEmptyRepresentations = representations.filter(
    (representation) => typeof representation === 'object' && 'VertexBuffer' in representation,
  );

  const visual3Ds: CADVisual3D[] = nonEmptyRepresentations.map((representation) => {
    const vertexBuffer = {
      positions: new Float32Array(parseVec3DArray(representation['VertexBuffer']['Positions']).flat()),
      normals: new Float32Array(parseVec3DArray(representation['VertexBuffer']['Normals']).flat()),
    };
    const faces = parseAsArray(representation['Faces']['Face']).map(parseFace);

    return { vertexBuffer, faces, center: computeCenter(vertexBuffer.positions) };
  });

  return visual3Ds;
}

/**
 * Recursive function that collects the children instance 3Ds and representation for
 * the requested reference3D id and add it to the Reference3D.
 * @param refId The reference id to construct the product branch for
 * @param reference3Ds Record with all the references of 3D objects
 * @param instance3Ds Record with all the instances of 3D objects
 * @param referenceReps Record with all the references of Representations
 * @param instanceReps Record with all the instances of Representations
 * @returns The product branch of the referenced id
 */
function constructTreeBranch(
  refId: number,
  reference3Ds: Record<number, Omit<CADComponent, 'children' | 'representations'>>,
  instance3Ds: Record<number, InstanceLink<CADComponentInstance>>,
  referenceReps: Record<number, CADBody>,
  instanceReps: Record<number, InstanceLink<CADBodyInstance>>,
): CADComponent {
  if (!(refId in reference3Ds)) {
    throw new Error('Could not find the reference id in the list');
  }

  const children = Object.values(instance3Ds)
    .filter((inst) => inst.isAggregatedBy === refId)
    .map(
      (inst): CADComponentInstance => ({
        name: inst.name,
        id: inst.id,
        instance: constructTreeBranch(inst.isInstanceOf, reference3Ds, instance3Ds, referenceReps, instanceReps),
        relativeMatrix: inst.relativeMatrix,
      }),
    );

  const representations = Object.values(instanceReps)
    .filter((inst) => inst.isAggregatedBy === refId)
    .map(
      (inst): CADBodyInstance => ({
        name: inst.name,
        id: inst.id,
        instance: referenceReps[inst.isInstanceOf],
      }),
    );

  return {
    name: reference3Ds[refId].name,
    id: reference3Ds[refId].id,
    children: reduceToMap(children),
    representations: representations,
  };
}

/**
 * Parses a 3DXML file and returns the retrieved product structure.
 * @param file A blob of the 3DXml file to parse
 * @returns The parsed 3DXML model
 */
export async function parse3DXML(file: Blob): Promise<CADModel> {
  const jszip = new JSZip();

  const asZip = await jszip.loadAsync(file);

  const manifestFile = asZip.file('Manifest.xml');
  const manifest = await parseManifestFile(manifestFile);

  const rootFile = asZip.file(manifest.rootFileName);
  const {
    rootId,
    referenceReps: referenceRepsRaw,
    reference3Ds,
    instanceReps,
    instance3Ds,
  } = await parseRootFile(rootFile);
  const referenceReps = reduceToMap(
    await Promise.all(
      Object.values(referenceRepsRaw).map(async (rr): Promise<CADBody> => {
        const repFile = asZip.file(rr.file);
        const visuals = await parse3DRepFile(repFile);

        return {
          id: rr.id,
          name: rr.name,
          visuals,
        };
      }),
    ),
  );

  return constructTreeBranch(rootId, reference3Ds, instance3Ds, referenceReps, instanceReps);
}
